import { select } from 'd3-selection';

import {
  COVERAGE_INSTALL_COLORS,
  COVERAGE_INSTALL_LABELS,
  COVERAGE_INSTALL_ORDER,
  COVERAGE_SUPPORT_FILL,
  COVERAGE_SUPPORT_LABELS,
  COVERAGE_SUPPORT_ORDER,
} from '../../lib/coverage/constants';
import type { CoverageInstance, CoverageWrapper } from '../../lib/coverage/types';
import { formatBytes, truncate } from '../../lib/formatBytes';
import { coverageTooltipHtml } from '../../lib/tooltipHtml';
import { coverageHexPoints } from './coverageHexPoints';

export interface CoverageRenderOptions {
  onHexClick: (inst: CoverageInstance) => void;
  showTooltip: (html: string, x: number, y: number) => void;
  hideTooltip: () => void;
}

function layoutCoverageWrappers(wrappers: CoverageWrapper[], W: number): number {
  const HEX_R = 11;
  const STRIDE_X = HEX_R * Math.sqrt(3);
  const STRIDE_Y = HEX_R * 1.5;

  const CARD_HDR_H = 38;
  const CARD_PAD_X = 12;
  const CARD_PAD_Y = 10;
  const CARD_GAP_X = 14;
  const CARD_GAP_Y = 14;
  const WRAP_HDR_H = 36;
  const WRAP_PAD_X = 16;
  const WRAP_PAD_Y = 14;
  const WRAP_GAP = 22;
  const TOP_PAD = 16;
  const SIDE_PAD = 12;
  const LEGEND_H = 36;

  wrappers.forEach((w) => {
    w.cards.forEach((card) => {
      const n = card.instances.length;
      const cols = Math.max(1, Math.ceil(Math.sqrt(n * 1.6)));
      const rows = Math.ceil(n / cols);
      card.cols = cols;
      card.rows = rows;
      card.gridW = cols * STRIDE_X + STRIDE_X / 2;
      card.gridH = rows * STRIDE_Y + HEX_R * 0.5;
      card.boxW = Math.max(card.gridW + CARD_PAD_X * 2, 200);
      card.boxH = CARD_HDR_H + card.gridH + CARD_PAD_Y * 2;
    });
  });

  const FLOW_W = Math.max(W - SIDE_PAD * 2, 600);
  const INNER_W = FLOW_W - WRAP_PAD_X * 2;
  let curY = TOP_PAD + LEGEND_H;

  wrappers.forEach((w) => {
    let nx = 0;
    let ny = 0;
    let rowH = 0;
    w.cards.forEach((card) => {
      const boxW = card.boxW ?? 0;
      const boxH = card.boxH ?? 0;
      if (nx + boxW > INNER_W && nx > 0) {
        nx = 0;
        ny += rowH + CARD_GAP_Y;
        rowH = 0;
      }
      card.relX = nx;
      card.relY = ny;
      nx += boxW + CARD_GAP_X;
      rowH = Math.max(rowH, boxH);
    });
    const innerH = ny + rowH;
    w.x = SIDE_PAD;
    w.y = curY;
    w.w = FLOW_W;
    w.h = WRAP_HDR_H + innerH + WRAP_PAD_Y * 2;
    curY += w.h + WRAP_GAP;
  });

  return curY + 16;
}

export function renderCoverageView(
  container: HTMLElement,
  wrappers: CoverageWrapper[],
  options: CoverageRenderOptions
): () => void {
  container.innerHTML = '';
  const W = container.clientWidth;
  if (!W || !wrappers.length) {
    container.innerHTML =
      '<div class="empty-state">No instances to show — try adjusting filters.</div>';
    return () => {};
  }

  const totalH = layoutCoverageWrappers(wrappers, W);

  const HEX_R = 11;
  const HEX_INNER = HEX_R - 0.6;
  const STRIDE_X = HEX_R * Math.sqrt(3);
  const STRIDE_Y = HEX_R * 1.5;
  const CARD_HDR_H = 38;
  const CARD_PAD_X = 12;
  const CARD_PAD_Y = 10;
  const WRAP_HDR_H = 36;
  const WRAP_PAD_X = 16;
  const WRAP_PAD_Y = 14;
  const TOP_PAD = 16;
  const SIDE_PAD = 12;

  const fillForInst = (inst: CoverageInstance) =>
    inst.support === 'supported'
      ? COVERAGE_INSTALL_COLORS[inst.install]
      : COVERAGE_SUPPORT_FILL[inst.support];
  const strokeForInst = (inst: CoverageInstance) =>
    inst.install === 'nothing' ? 'none' : COVERAGE_INSTALL_COLORS[inst.install];

  const svg = select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', totalH)
    .attr('viewBox', `0 0 ${W} ${totalH}`)
    .style('display', 'block');

  const legend = svg.append('g').attr('transform', `translate(${SIDE_PAD},${TOP_PAD - 4})`);
  let lx = 0;
  COVERAGE_INSTALL_ORDER.forEach((key) => {
    const color = COVERAGE_INSTALL_COLORS[key];
    const grp = legend.append('g').attr('transform', `translate(${lx}, 0)`);
    grp
      .append('polygon')
      .attr('points', coverageHexPoints(7))
      .attr('transform', 'translate(8, 9)')
      .attr('fill', color)
      .attr('stroke', key === 'nothing' ? 'none' : color)
      .attr('stroke-width', 1.2);
    grp
      .append('text')
      .attr('x', 22)
      .attr('y', 13)
      .attr('font-size', '12px')
      .attr('fill', 'var(--color-gray-700)')
      .attr('font-family', 'var(--font-sans)')
      .text(COVERAGE_INSTALL_LABELS[key]);
    lx += 24 + COVERAGE_INSTALL_LABELS[key].length * 6.7 + 16;
  });
  legend
    .append('line')
    .attr('x1', lx - 4)
    .attr('x2', lx - 4)
    .attr('y1', 2)
    .attr('y2', 16)
    .attr('stroke', 'var(--color-gray-300)')
    .attr('stroke-width', 1);
  lx += 8;
  COVERAGE_SUPPORT_ORDER.forEach((key) => {
    const grp = legend.append('g').attr('transform', `translate(${lx}, 0)`);
    grp
      .append('polygon')
      .attr('points', coverageHexPoints(7))
      .attr('transform', 'translate(8, 9)')
      .attr('fill', COVERAGE_SUPPORT_FILL[key])
      .attr('stroke', 'rgba(0,0,0,0.2)')
      .attr('stroke-width', 0.6);
    grp
      .append('text')
      .attr('x', 22)
      .attr('y', 13)
      .attr('font-size', '12px')
      .attr('fill', 'var(--color-gray-700)')
      .attr('font-family', 'var(--font-sans)')
      .text(COVERAGE_SUPPORT_LABELS[key]);
    lx += 24 + COVERAGE_SUPPORT_LABELS[key].length * 6.7 + 16;
  });

  wrappers.forEach((w) => {
    const wG = svg.append('g').attr('transform', `translate(${w.x},${w.y})`);
    const wrapColor = w.kind === 'ec2' ? 'var(--color-dark-magenta)' : 'var(--color-vivd-purple)';
    const wrapBg = w.kind === 'ec2' ? 'var(--color-magnolia-accent)' : 'var(--color-magnolia)';

    wG.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', w.w ?? 0)
      .attr('height', w.h ?? 0)
      .attr('rx', 12)
      .attr('fill', wrapBg)
      .attr('fill-opacity', 0.35)
      .attr('stroke', wrapColor)
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1);

    wG.append('text')
      .attr('x', WRAP_PAD_X)
      .attr('y', 23)
      .attr('font-size', '14px')
      .attr('font-weight', 600)
      .attr('fill', wrapColor)
      .attr('font-family', 'var(--font-display)')
      .text(w.name);

    wG.append('text')
      .attr('x', (w.w ?? 0) - WRAP_PAD_X)
      .attr('y', 23)
      .attr('text-anchor', 'end')
      .attr('font-size', '11.5px')
      .attr('fill', 'var(--color-gray-700)')
      .attr('font-family', 'var(--font-sans)')
      .text(
        `${w.totalInstances} ${w.totalInstances === 1 ? 'node' : 'nodes'} · ${formatBytes(w.supportedSize)} potential · ${w.cards.length} ${w.cards.length === 1 ? 'group' : 'groups'}`
      );

    const stage = wG.append('g').attr('transform', `translate(${WRAP_PAD_X},${WRAP_HDR_H + WRAP_PAD_Y})`);

    w.cards.forEach((card) => {
      const boxW = card.boxW ?? 0;
      const boxH = card.boxH ?? 0;
      const cols = card.cols ?? 1;
      const cG = stage
        .append('g')
        .attr('transform', `translate(${card.relX},${card.relY})`)
        .attr('cursor', 'pointer');

      cG.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', boxW)
        .attr('height', boxH)
        .attr('rx', 8)
        .attr('fill', 'var(--color-white)')
        .attr('stroke', 'var(--color-primary-100)')
        .attr('stroke-width', 1);

      cG.append('text')
        .attr('x', CARD_PAD_X)
        .attr('y', 18)
        .attr('font-size', '12.5px')
        .attr('font-weight', 600)
        .attr('fill', 'var(--color-primary-900)')
        .attr('font-family', 'var(--font-display)')
        .text(truncate(card.name, Math.max(20, Math.floor(boxW / 7))));

      cG.append('text')
        .attr('x', CARD_PAD_X)
        .attr('y', 33)
        .attr('font-size', '11px')
        .attr('fill', 'var(--color-gray-600)')
        .attr('font-family', 'var(--font-sans)')
        .text(
          `${card.instances.length} ${card.instances.length === 1 ? 'node' : 'nodes'} · ${formatBytes(card.supportedSize)} potential`
        );

      let chipX = boxW - CARD_PAD_X;
      [...COVERAGE_INSTALL_ORDER].reverse().forEach((k) => {
        if (!card.installCounts?.[k]) return;
        const color = COVERAGE_INSTALL_COLORS[k];
        const txt = String(card.installCounts[k]);
        const cw = txt.length * 6.5 + 22;
        const chip = cG.append('g').attr('transform', `translate(${chipX - cw}, 8)`);
        chip
          .append('rect')
          .attr('width', cw)
          .attr('height', 18)
          .attr('rx', 9)
          .attr('fill', color)
          .attr('fill-opacity', 0.14)
          .attr('stroke', color)
          .attr('stroke-opacity', 0.45)
          .attr('stroke-width', 1);
        chip
          .append('polygon')
          .attr('points', coverageHexPoints(5))
          .attr('transform', 'translate(10, 9)')
          .attr('fill', color)
          .attr('stroke', k === 'nothing' ? 'none' : color)
          .attr('stroke-width', 0.6);
        chip
          .append('text')
          .attr('x', 18)
          .attr('y', 13)
          .attr('font-size', '10.5px')
          .attr('font-weight', 600)
          .attr('fill', 'var(--color-gray-800)')
          .attr('font-family', 'var(--font-sans)')
          .text(txt);
        chipX -= cw + 4;
      });

      const hexG = cG.append('g').attr('transform', `translate(${CARD_PAD_X},${CARD_HDR_H + CARD_PAD_Y})`);
      card.instances.forEach((inst, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const offset = (row % 2) * (STRIDE_X / 2);
        const cx = col * STRIDE_X + offset + HEX_R;
        const cy = row * STRIDE_Y + HEX_R;
        hexG
          .append('polygon')
          .attr('points', coverageHexPoints(HEX_INNER))
          .attr('transform', `translate(${cx},${cy})`)
          .attr('fill', fillForInst(inst))
          .attr('stroke', strokeForInst(inst))
          .attr('stroke-width', 1.4)
          .attr('cursor', 'pointer')
          .on('mouseenter', (event) => {
            select(event.currentTarget)
              .attr('stroke', 'var(--color-primary-500)')
              .attr('stroke-width', 2.4)
              .raise();
            options.showTooltip(coverageTooltipHtml(inst), event.clientX, event.clientY);
          })
          .on('mouseleave', (event) => {
            select(event.currentTarget)
              .attr('stroke', strokeForInst(inst))
              .attr('stroke-width', 1.4);
            options.hideTooltip();
          })
          .on('click', (event) => {
            event.stopPropagation();
            options.onHexClick(inst);
          });
      });
    });
  });

  return () => {
    container.innerHTML = '';
  };
}
